import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewContainerRef
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { TimepickerTemplateComponent } from './timepicker-template';
import { TimepickerComponent } from './timepicker.component';
import { TimepickerService } from './timepicker.shared.service';
import { min, max } from './timepicker.validator';

export const TIMEPICKER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AxisTimePickerComponent),
  multi: true
};

export const TIMEPICKER_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => AxisTimePickerComponent),
  multi: true
};

@Component({
  selector: 'axis-timepicker,[axis-timepicker]',
  templateUrl: './axis-timepicker.component.html',
  host: { class: 'axis-timepicker-wrapper' },
  providers: [TIMEPICKER_CONTROL_VALUE_ACCESSOR, TIMEPICKER_VALIDATORS]
})
export class AxisTimePickerComponent
  implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {

  @Input() arrowkeys: boolean; // if false up/down arrowkeys inside hours,minutes,seconds will be disabled.
  @Input() closeOnOutsideClick = true;
  @Input() disabled = false;
  @Input() font = 'custom';  // font for axis-icon.

  @Input() hourStep: number;  // hours change step.
  @Input() minuteStep: number;  // minutes change step.
  @Input() secondsStep: number;  // seconds change step.

  @Input() max: Date;  // maximum time user can select.
  @Input() min: Date;  // minimum time user can select.

  @Input() meridians: string[] = ['AM', 'PM'];  // meridian labels based on locale.
  @Input() public open = false;
  @Input() required = false;
  @Input() showSpinners = true;  // if true spinner arrows above and below the inputs will be shown.
  @Input() showMeridian = true;  // if true, meridians AM/PM button will be shown.
  @Input() showSeconds = false;  // if true, seconds will be shown.

  // @HostBinding('class.axis-timepicker-wrapper') axisTimepickerWrapper = true;

  @Output() onTimepickerClose = new EventEmitter();

  public componentRef: any;
  public modelValue: Date;
  public placeHolder: string;
  public timepickerFormat: string;
  public top: any;

  private containerEl = document.querySelector('body');
  private scrollYPosition: any;
  private timePickerRef = [];
  private timePickerComponentRef: any;
  private unbindDocumentEvent: Function;
  private validator: any;

  public onTouched: any = Function.prototype;
  public onChange: any = Function.prototype;

  constructor(
    private el: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private resolver: ComponentFactoryResolver,
    private timepickerService: TimepickerService,
    private renderer: Renderer2
  ) { }

  ngOnInit() {
    if (this.open) {
      this.createTimepickerComponent();
    }
    this.changeTimeFormat();
  }

  ngOnChanges() {
    this.changeTimeFormat();
  }

  onValueChange(e) {
    this.onTouched();
  }

  // for ngModel implementing custom form ControlValueAccessor
  writeValue(value) {
    if (value) {
      this.modelValue = value;
    } else {
      this.modelValue = null;
    }
  }

  registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): { [key: string]: any } {
    if (this.required) {
      if (control.value) {
        if (this.max) {
          this.validator = max(this.max);
          if (this.validator(control)) {
            return this.validator(control);
          } else if (this.min) {
            this.validator = min(this.min);
            return this.validator(control);
          }
        }
        return null;
      }
      return { axistimepicker: { valid: false } };
    } else {
      if (control.value) {
        if (this.max) {
          this.validator = max(this.max);
          if (this.validator(control)) {
            return this.validator(control);
          } else if (this.min) {
            this.validator = min(this.min);
            return this.validator(control);
          }
        }
        return null;
      }
      return null;
    }
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  disableTimepicker() {
    this.disabled = true;
  }

  enableTimepicker() {
    this.disabled = false;
  }

  toggleTimepicker() {
    this.disabled = !this.disabled;
  }

  public getTime(): number {
    if (this.modelValue) {
      return this.modelValue && this.modelValue.getTime();
    }
  }

  changeTimeFormat() {
    if (this.showSeconds) {
      if (this.showMeridian === true) {
        this.timepickerFormat = 'hh:mm:ss a';
      } else {
        this.timepickerFormat = 'HH:mm:ss';
      }
      this.placeHolder = 'HH:MM:SS';
    } else {
      if (this.showMeridian === true) {
        this.timepickerFormat = 'shortTime';
      } else {
        this.timepickerFormat = 'HH:mm';
      }
      this.placeHolder = 'HH:MM';
    }
  }

  createTimepickerComponent() {
    if (!this.timePickerComponentRef) {
      this.timepickerService.setTimepickerInstance(this);
      const resolverFactory = this.resolver.resolveComponentFactory(
        TimepickerComponent
      );
      this.timePickerComponentRef = this.viewContainerRef.createComponent(
        resolverFactory
      );
      this.displayTimepicker();
      this.timePickerComponentRef.instance.closeEvent.subscribe(data => {
        this.modelValue = data;
        this.onChange(data);
        this.removeTimePicker();
      });
      this.timePickerComponentRef.instance.selectionDone.subscribe(data => {
        this.modelValue = data;
        this.onChange(data);
        this.removeTimePicker();
      });
      if (this.closeOnOutsideClick) {
        this.addDocumentListener();
      }
    }
  }

  displayTimepicker() {
    this.scrollYPosition = !document.body.classList.contains(
      'axis-tooltip-open'
    )
      ? window.pageYOffset
      : parseInt(document.body.style.top, 10) * -1;
    if (!document.body.classList.contains('axis-tooltip-open')) {
      document.body.style.top = -this.scrollYPosition + 'px';
      document.body.style.position = 'fixed';
      if (document.body.scrollHeight > window.innerHeight) {
        document.body.classList.add('axis-timepicker-open');
      } else {
        document.body.classList.add('axis-timepicker-hidden-scroll');
      }
    }
    const componentFactory = this.resolver.resolveComponentFactory(
      TimepickerTemplateComponent
    );
    this.componentRef = componentFactory.create(this.injector);
    this.applicationRef.attachView(this.componentRef.hostView);
    this.componentRef.location.nativeElement.appendChild(
      this.timePickerComponentRef.location.nativeElement
    );
    this.containerEl.appendChild(this.componentRef.location.nativeElement);
    this.calculatePosition();
  }

  hideTimepicker() {
    if (this.componentRef) {
      const menuElement = this.componentRef.location.nativeElement;
      if (menuElement && menuElement.parentNode) {
        menuElement.parentNode.removeChild(menuElement);
      }
      this.componentRef.destroy();
    }
    if (!document.body.classList.contains('axis-tooltip-open')) {
      document.body.classList.remove('axis-timepicker-open');
      document.body.classList.remove('axis-timepicker-hidden-scroll');
      document.body.style.position = '';
      window.scrollTo(0, this.scrollYPosition);
    }
  }

  openTimePicker(event: Event) {
    event.preventDefault();
    this.createTimepickerComponent();
    this.timepickerService.inputTimeElement(this.el);
    this.keyboardNavigation();
  }

  closeTimePicker(event: any): boolean {
    if (this.timePickerComponentRef) {
      if (
        !this.timePickerComponentRef.location.nativeElement.contains(
          event.target
        ) &&
        !this.el.nativeElement.contains(event.target)
      ) {
        return true;
      } else {
        return false;
      }
    }
  }

  removeTimePicker() {
    if (this.timePickerComponentRef) {
      this.timePickerComponentRef.instance.closeEvent.unsubscribe();
      this.timePickerComponentRef.instance.selectionDone.unsubscribe();
      this.timePickerComponentRef.destroy();
      this.timePickerComponentRef = null;
      this.hideTimepicker();
      if (typeof this.unbindDocumentEvent === 'function') {
        this.unbindDocumentEvent();
      }
      this.timepickerService.destroyData();
      this.onTimepickerClose.emit(this.modelValue);
    }
  }

  addDocumentListener() {
    this.unbindDocumentEvent = this.renderer.listen(
      document,
      'click',
      (event: Event) => {
        event.stopPropagation();
        if (this.closeTimePicker(event) && (event.type !== 'key')) {
          this.removeTimePicker();
        }
      }
    );
  }

  calculatePosition() {
    const hostElement = this.el.nativeElement;
    const menuElement = this.timePickerComponentRef.location.nativeElement;
    setTimeout(() => {
      const posObj = hostElement.getBoundingClientRect();
      const menuPosObj = menuElement.getBoundingClientRect();
      this.smartPositioning(posObj, menuPosObj);
    }, 0);
    this.keyboardNavigation(); // keyboard navigation on timepicker container
  }

  // keyboard navigation on timepicker container
  keyboardNavigation() {
    const focusable = document.querySelector('.axis-timepicker-container')
      .querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const focusableArray = [];
    Array.from(focusable).forEach(function (element) {
      focusableArray.push(element);
    });
    const firstFocusable = focusableArray[2]; // first focussable item in timepicker container
    const lastFocusable = focusableArray[focusable.length - 1]; // last focussable item in timepicker container
    firstFocusable.focus();
    this.renderer.listen(firstFocusable, 'keydown', (event) => { // triggers on first focussable item
      const key = event.key.toLocaleLowerCase();
      if (event.shiftKey && key === 'tab') {
        event.preventDefault();
        lastFocusable.focus();
      }
    });
    this.renderer.listen(lastFocusable, 'keydown', (event) => { // triggers on last focussable item
      const key = event.key.toLocaleLowerCase();
      if (!event.shiftKey && key === 'tab') {
        event.preventDefault();
        firstFocusable.focus();
      }
    });
  }

  // key down handler for enter and space
  keyDownHandler(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.code === 'Space' || event.keyCode === 32 || event.keyCode === 13) {
      event.preventDefault();
      this.createTimepickerComponent();
      this.timepickerService.inputTimeElement(this.el);
      this.keyboardNavigation(); // keyboard navigation on timepicker container
    }
  }

  // for escape key to close the timepicker
  @HostListener('window:keyup', ['$event'])
  onKeyup(event: any) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.removeTimePicker();
    }
  }

  smartPositioning(posObj: any, menuPosObj: any) {
    const menuHeight = menuPosObj.height;
    if (
      window.innerHeight - (posObj.bottom + menuHeight) > 0 ||
      posObj.top - menuHeight < 0
    ) {
      this.bottomPositioning(posObj, menuPosObj);
    } else {
      this.topPositioning(posObj, menuPosObj, menuHeight);
    }
  }

  topPositioning(posObj: any, menuPosObj: any, menuHeight: number) {
    const top = posObj.top - menuHeight + this.scrollYPosition - 3;
    if (window.innerWidth - (posObj.left + menuPosObj.width) > 0) {
      const left = posObj.left;
      this.timePickerComponentRef.location.nativeElement.style.left =
        left + 'px';
    } else {
      const right = window.innerWidth - posObj.right;
      this.timePickerComponentRef.location.nativeElement.style.right =
        right + 'px';
    }
    this.timePickerComponentRef.location.nativeElement.style.top = top + 'px';
  }

  bottomPositioning(posObj: any, menuPosObj: any) {
    const top = posObj.bottom + this.scrollYPosition;
    if (window.innerWidth - (posObj.left + menuPosObj.width) > 0) {
      const left = posObj.left;
      this.timePickerComponentRef.location.nativeElement.style.left =
        left + 'px';
    } else {
      const right = window.innerWidth - posObj.right;
      this.timePickerComponentRef.location.nativeElement.style.right =
        right + 'px';
    }
    this.timePickerComponentRef.location.nativeElement.style.top = top + 'px';
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }
}
